<!-- eslint-disable vue/multi-word-component-names -- Existing component, kept for backward compatibility -->
<!--
    MomentsWithConnections.vue
    This is the new "With Connections" version of the Moments component.
    Feature-flagged via: performance_management_connections_integration
-->
<template>
    <div class="flex flex-col gap-4">
        <!-- Timeline navigation -->
        <div class="max-w-4xl mx-auto w-full px-4">
            <div class="flex gap-4 w-full items-stretch" role="tablist" aria-orientation="horizontal">
                <MomentsTimeline
                    v-for="moment in moments"
                    :key="moment.id"
                    :moment-id="moment.id"
                    :moment-title="moment.title"
                    :date="formatDate(moment.start_date)"
                    :state="moment.state"
                    :is-active="activeMomentId === moment.id"
                    :is-preview="moment.visibility === 'preview'"
                    @activate="setActiveMoment" />
            </div>
        </div>

        <NoMomentsNotification v-if="moments.length === 0" />

        <PerformanceMomentEndOfYear
            v-if="activeMoment"
            :key="activeMoment.id"
            :moment="activeMoment"
            :has-people-to-review="hasPeopleToReview"
            :show-coach-notification="showNadiaTeamsEvalNotification && !isMobileApp" />

        <template v-if="organizationCards.length > 0">
            <div>
                <h3 class="title-body mb-10">{{ t("moments.page.organizationTopicsHeading", { organizationName }) }}</h3>
                <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <NewHomeCard
                        v-for="card in organizationCards"
                        :key="card.id"
                        :card-type="card.type"
                        :title="card.title"
                        :subtitle="card.subtitle ?? undefined"
                        :content="card.content"
                        :cta-button-text="card.ctaButtonText"
                        @cta-click="handleOrganizationCardClick(card)">
                        <template #header>
                            <img v-if="card.image_url" :src="card.image_url" :alt="card.title || t('moments.page.cardImageAlt')" class="h-10 w-auto object-contain max-w-50" />
                            <img
                                v-else-if="organizationDetails.logo_url"
                                :src="organizationDetails.logo_url"
                                :alt="organizationDetails.name || t('moments.page.organizationLogoAlt')"
                                class="h-10 w-auto object-contain max-w-50" />
                            <i v-else class="bi bi-people text-3xl/none" aria-hidden="true"></i>
                        </template>
                    </NewHomeCard>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { usePage } from "@inertiajs/vue3";
import NewHomeCard from "~vue/ChatHome/NewHomeCard.vue";
import MomentsTimeline from "~vue/components/moments/MomentsTimeline.vue";
import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "~vue/Moments/constants";
import NoMomentsNotification from "~vue/Moments/NoMomentsNotification.vue";
import PerformanceMomentEndOfYear from "~vue/Moments/PerformanceMomentEndOfYear.vue";
import type { OrganizationCard } from "~vue/types/chat_home";
import type { PerformanceMoment as PerformanceMomentType } from "~vue/types/performance_management";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHATSOURCE, HOMEPAGE_CARD_LOCATION } from "~vue/utils/constants";
import { formatDate as localizedDate } from "~vue/utils/formatDate";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, type Ref, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    moments: PerformanceMomentType[];
    organizationCards?: OrganizationCard[];
    showNadiaTeamsEvalNotification?: boolean;
    hasPeopleToReview: boolean | null;
}

const props = withDefaults(defineProps<Props>(), {
    organizationCards: () => [],
    showNadiaTeamsEvalNotification: false,
});

const { t } = useI18n();

const getInitialMomentSelection = () => {
    // Deep link from the homepage banner's per-moment buttons (ENG-1330)
    const momentIdParam = new URLSearchParams(globalThis.location.search).get("moment_id");
    if (momentIdParam) {
        const requestedMoment = props.moments.find((m) => m.id === Number(momentIdParam) && m.state !== "locked");
        if (requestedMoment) {
            return requestedMoment;
        }
    }
    const latestMoments = [...props.moments].reverse();
    return (
        latestMoments.find((m) => m.state === "open") || latestMoments.find((m) => m.state === "completed") || latestMoments.find((m) => m.state === "expired") || props.moments[0]
    );
};
const defaultActiveMoment = getInitialMomentSelection();
const activeMomentId = ref<number | null>(defaultActiveMoment?.id || null);
const isMobileApp = usePage().props?.is_mobile_app;

const setActiveMoment = (momentId: number) => {
    activeMomentId.value = momentId;
};

watch(activeMomentId, (moment_id) => {
    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}moment_clicked`, { moment_id });
});

const activeMoment = computed(() => {
    return props.moments.find((m) => m.id === activeMomentId.value);
});

const formatDate = (date: string): string => {
    if (!date) {
        return "";
    }
    return localizedDate(date, "month-year");
};

interface OrganizationDetails {
    is_default?: boolean;
    name?: string;
    logo_url?: string;
}

const organizationDetails = inject<Ref<OrganizationDetails>>("organization_details", ref({}));
const organizationName = computed(() => {
    if (organizationDetails.value?.is_default || !organizationDetails.value?.name) {
        return t("moments.page.defaultOrganizationName");
    }
    return organizationDetails.value.name;
});
const handleOrganizationCardClick = (card: OrganizationCard) => {
    logUserInteraction(AmplitudeEventNames.HOMEPAGE_CARD_CLICKED, { card_type: card.type, location: HOMEPAGE_CARD_LOCATION.MOMENTS });
    openUrl(card.url, { queryParams: { chat_source: CHATSOURCE.TRENDING_ORG_PROMPT } });
};

// The removed "Looking for your old conversations?" toggle persisted reverted moment IDs
// under this key; clear it so stale data doesn't linger in browsers.
globalThis.localStorage?.removeItem("revert_to_old_ui_moment_ids");
</script>
